<script setup lang="ts">
import { useCourseExemptions } from '@/composables/queries/useCourseExemptions';
import { useCourseExemptionMutations } from '@/composables/mutations/tertiary/useCourseExemptionMutations';
import { useConfirm } from 'primevue/useconfirm';
import AddExemptionDialog from './AddExemptionDialog.vue';
import BulkExemptByStudentsDialog from './BulkExemptByStudentsDialog.vue';
import BulkExemptByScopeDialog from './BulkExemptByScopeDialog.vue';
import BulkExemptFromExcelDialog from './BulkExemptFromExcelDialog.vue';
import CourseWaivers from './CourseWaivers.vue';
import ManualOutstandingCourses from './ManualOutstandingCourses.vue';

const viewMode = ref<'exemptions' | 'waivers' | 'manual'>('exemptions');
const viewOptions = [
  { label: 'Permanent Exemptions', value: 'exemptions' },
  { label: 'Temporary Waivers', value: 'waivers' },
  { label: 'Manual Outstanding', value: 'manual' },
];

const confirm = useConfirm();
const { exemptions, loading, refetch, setFilter, clearFilter } = useCourseExemptions();
const {
  bulkUpdateStudentCourseExemption,
  bulkDeleteStudentCourseExemption,
  updateStudentCourseExemption,
  bulkUpdating,
  bulkDeleting,
  updatingExemption,
} = useCourseExemptionMutations();

// Selection
const selectedRows = ref<Array<Record<string, unknown>>>([]);

// Filter state
const filterMatric = ref('');
const filterCourseCode = ref('');
const filterProgramme = ref('');
const filterDepartment = ref('');
const filterFaculty = ref('');

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

function onFilterChange() {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    setFilter({
      matric_no: filterMatric.value || undefined,
      course_code: filterCourseCode.value || undefined,
      program_name: filterProgramme.value || undefined,
      department_name: filterDepartment.value || undefined,
      faculty_name: filterFaculty.value || undefined,
    });
  }, 400);
}

function resetFilters() {
  filterMatric.value = '';
  filterCourseCode.value = '';
  filterProgramme.value = '';
  filterDepartment.value = '';
  filterFaculty.value = '';
  clearFilter();
}

const hasActiveFilters = computed(() =>
  filterMatric.value || filterCourseCode.value || filterProgramme.value || filterDepartment.value || filterFaculty.value,
);

// Dialogs
const showAddDialog = ref(false);
const showBulkByStudents = ref(false);
const showBulkByScope = ref(false);
const showBulkFromExcel = ref(false);

// Bulk actions
async function handleBulkActivate() {
  const items = selectedRows.value.map((row) => ({ id: row.id as string, deleted: 1 }));
  const result = await bulkUpdateStudentCourseExemption(items);
  if (result?.success) {
    selectedRows.value = [];
    await refetch();
  }
}

async function handleBulkSuspend() {
  const items = selectedRows.value.map((row) => ({ id: row.id as string, deleted: 0 }));
  const result = await bulkUpdateStudentCourseExemption(items);
  if (result?.success) {
    selectedRows.value = [];
    await refetch();
  }
}

function handleBulkDelete() {
  confirm.require({
    message: `Are you sure you want to delete ${selectedRows.value.length} exemption(s)? This action cannot be undone.`,
    header: 'Confirm Delete',
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    acceptClass: 'p-button-danger',
    accept: async() => {
      const ids = selectedRows.value.map((row) => row.id as string);
      const result = await bulkDeleteStudentCourseExemption(ids);
      if (result?.success) {
        selectedRows.value = [];
        await refetch();
      }
    },
  });
}

// Row actions
async function toggleRowStatus(row: Record<string, unknown>) {
  const newDeleted = row.deleted === 0 ? 1 : 0;
  const result = await updateStudentCourseExemption(row.id as string, { deleted: newDeleted });
  if (result?.success) {
    await refetch();
  }
}

// After dialog success
async function onMutationSuccess() {
  await refetch();
}

watch([filterMatric, filterCourseCode, filterProgramme, filterDepartment, filterFaculty], () => {
  onFilterChange();
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- View Toggle -->
    <div class="flex items-center gap-3">
      <SelectButton v-model="viewMode" :options="viewOptions" optionLabel="label" optionValue="value" :allowEmpty="false" />
    </div>

    <!-- Temporary Waivers Tab -->
    <CourseWaivers v-if="viewMode === 'waivers'" />

    <!-- Manual Outstanding Tab -->
    <ManualOutstandingCourses v-if="viewMode === 'manual'" />

    <!-- Permanent Exemptions Tab -->
    <template v-if="viewMode === 'exemptions'">

    <!-- Action Buttons -->
    <div class="flex flex-wrap items-center gap-2">
      <Button data-testid="exemption-add-btn" label="Add Exemption" icon="pi pi-plus" size="small" @click="showAddDialog = true" />
      <Button data-testid="exemption-bulk-students-btn" label="Bulk by Students" icon="pi pi-users" size="small" severity="secondary" outlined @click="showBulkByStudents = true" />
      <Button data-testid="exemption-bulk-scope-btn" label="Bulk by Scope" icon="pi pi-sitemap" size="small" severity="secondary" outlined @click="showBulkByScope = true" />
      <Button data-testid="exemption-import-excel-btn" label="Import from Excel" icon="pi pi-file-excel"
        size="small" severity="secondary" outlined @click="showBulkFromExcel = true" />
    </div>

    <!-- Filters -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
      <div>
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Matric No</label>
        <InputText data-testid="exemption-filter-matric" v-model="filterMatric" placeholder="Search matric..." size="small" class="w-full rounded-lg" />
      </div>
      <div>
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Course Code</label>
        <InputText data-testid="exemption-filter-course" v-model="filterCourseCode" placeholder="Search course..." size="small" class="w-full rounded-lg" />
      </div>
      <div>
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Programme</label>
        <InputText data-testid="exemption-filter-programme" v-model="filterProgramme" placeholder="Programme..." size="small" class="w-full rounded-lg" />
      </div>
      <div>
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Department</label>
        <InputText data-testid="exemption-filter-department" v-model="filterDepartment" placeholder="Department..." size="small" class="w-full rounded-lg" />
      </div>
      <div>
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Faculty</label>
        <InputText data-testid="exemption-filter-faculty" v-model="filterFaculty" placeholder="Faculty..." size="small" class="w-full rounded-lg" />
      </div>
    </div>
    <div v-if="hasActiveFilters" class="flex justify-end">
      <Button data-testid="exemption-filter-clear" label="Clear" icon="pi pi-times" size="small" text severity="secondary" @click="resetFilters" />
    </div>

    <!-- Bulk Action Bar -->
    <div v-if="selectedRows.length > 0"
      class="bg-primary/10 border border-primary/20 rounded-lg p-3 flex items-center justify-between">
      <span class="text-sm text-primary-700 font-medium">
        {{ selectedRows.length }} exemption(s) selected
      </span>
      <div class="flex gap-2">
        <Button data-testid="exemption-bulk-activate" label="Activate" icon="pi pi-play" size="small" severity="success" outlined
          @click="handleBulkActivate" :loading="bulkUpdating" />
        <Button data-testid="exemption-bulk-suspend" label="Suspend" icon="pi pi-pause" size="small" severity="warn" outlined
          @click="handleBulkSuspend" :loading="bulkUpdating" />
        <Button data-testid="exemption-bulk-delete" label="Delete" icon="pi pi-trash" size="small" severity="danger" outlined
          @click="handleBulkDelete" :loading="bulkDeleting" />
        <Button data-testid="exemption-bulk-clear" label="Clear" icon="pi pi-times-circle" size="small" severity="secondary" text
          @click="selectedRows = []" />
      </div>
    </div>

    <!-- Data Table -->
    <DataTable
      size="small"
      data-testid="exemption-table"
      :value="exemptions"
      :loading="loading"
      v-model:selection="selectedRows"
      dataKey="id"
      stripedRows
      tableStyle="min-width: 50rem"
    >
      <template #empty>No exemptions found.</template>
      <Column selectionMode="multiple" headerStyle="width: 3rem" />
      <Column header="Student" sortable>
        <template #body="{ data }">
          <div class="flex flex-col">
            <span class="font-medium">{{ data.student?.surname }} {{ data.student?.first_name }}</span>
            <span class="text-xs text-gray-500">{{ data.student?.matric_no }}</span>
          </div>
        </template>
      </Column>
      <Column header="Course" sortable>
        <template #body="{ data }">
          <div class="flex flex-col">
            <span class="font-medium">{{ data.course?.course_code }}</span>
            <span class="text-xs text-gray-500">{{ data.course?.course_title }}</span>
          </div>
        </template>
      </Column>
      <Column header="Units" style="width: 80px">
        <template #body="{ data }">
          {{ data.course?.unit }}
        </template>
      </Column>
      <Column header="Status" style="width: 100px">
        <template #body="{ data }">
          <Tag :value="data.deleted === 1 ? 'Exempted' : 'Suspended'" :severity="data.deleted === 1 ? 'success' : 'warn'" />
        </template>
      </Column>
      <Column header="Actions" style="width: 100px">
        <template #body="{ data }">
          <Button
            :data-testid="`exemption-toggle-${data.id}`"
            :icon="data.deleted === 1 ? 'pi pi-pause' : 'pi pi-play'"
            :severity="data.deleted === 1 ? 'warn' : 'success'"
            size="small"
            text
            rounded
            :loading="updatingExemption"
            @click="toggleRowStatus(data)"
            :title="data.deleted === 1 ? 'Suspend exemption (course will appear as outstanding)' : 'Activate exemption (course will be excluded from outstanding)'"
          />
        </template>
      </Column>
    </DataTable>

    <!-- Result cap warning -->
    <Message v-if="exemptions.length >= 50" severity="info" :closable="false" class="mt-0">
      Showing first 50 results. Use filters to narrow down.
    </Message>

    <!-- Dialogs -->
    <ConfirmDialog />
    <AddExemptionDialog v-model:visible="showAddDialog" @success="onMutationSuccess" />
    <BulkExemptByStudentsDialog v-model:visible="showBulkByStudents" @success="onMutationSuccess" />
    <BulkExemptByScopeDialog v-model:visible="showBulkByScope" @success="onMutationSuccess" />
    <BulkExemptFromExcelDialog v-model:visible="showBulkFromExcel" @success="onMutationSuccess" />

    </template>
  </div>
</template>
